import React from "react";
import { useSelector } from "react-redux";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import i18n from "@microdoing/shared/i18n";
import moment from "moment";
import { IStepFeedback } from "@microdoing/shared/services/userCampaign/types";
import { defaultStepFeedback } from "@microdoing/shared/services/userCampaign/defaultStates";
import selectLanguage from "@microdoing/shared/utils/selectLanguage";
import {
  Box,
  MainText,
  PressableOpacity,
  SkillLabel,
} from "app/components/common";
import theme from "app/Theme/Theme";
import removeHtmlTags from "@microdoing/shared/utils/removeHtmlTags";
import ArrowRightSlider from "app/components/Icons/ArrowSlider/ArrowRightSlider";
import normalize from "app/utils/normalize";
import { getHasCampaignReviews } from "@microdoing/shared/services/userCampaign/selectors";
import { Image } from "react-native";
import { getRate } from "app/utils/getRating";
import FastImage from "react-native-fast-image";
import { StarImage } from "../FeedbackScreen/ReflexiveAnalysis/ActionRatingScreen/RatingStar";
import { getStatusColor } from "app/utils/getStatusReview";
import MediaIcon from "app/components/Icons/MediaIcon";
import ReviewStatusChip from "./ReviewStatusChip";

interface Props {
  onPress: () => void;
  index?: number;
  feedback: IStepFeedback;
}

const HistoricalBox = ({
  onPress,
  feedback = defaultStepFeedback,
  index = 0,
}: Props) => {
  const language = useSelector(getLanguage);
  const hasReview = useSelector(getHasCampaignReviews);

  const {
    files = [],
    createdAt,
    donePercentage,
    rating,
    stepId = { title: {}, content: { title: {} }, category: "" },
    status = "new",
  } = feedback;

  const { title, content, category, skills = [] } = stepId;
  const skill = skills?.[0] || "";

  const getRatingComponent = () => {
    const rate = getRate(donePercentage);

    return (
      <Box flexDirection="row" alignItems="center" marginRight="sm">
        <Image
          style={{
            width: normalize(25),
            height: normalize(25),
          }}
          source={rate.smiley}
        />
        <MainText variant="l1-medium">{rate.name}</MainText>
      </Box>
    );
  };

  return (
    <PressableOpacity onPress={onPress} testID={`feedback-box-${index}`}>
      <Box
        borderRadius={8}
        backgroundColor="neutral.white"
        paddingVertical="sm"
        paddingHorizontal="m"
        marginBottom="sm"
        shadowColor="neutral.100"
        shadowOffset={{ width: 0, height: 4 }}
        shadowOpacity={1}
        shadowRadius={4}
      >
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          marginBottom="xxs"
        >
          <MainText variant="l5-bold" paddingRight="m" numberOfLines={1}>
            {selectLanguage({ text: title, language })}
          </MainText>
          <ArrowRightSlider color={theme.colors["brand.500"]} />
        </Box>
        <Box flexDirection="row" marginBottom="xs">
          <MainText variant="l1-regular" color="neutral.600" marginRight="xxs">
            {moment(createdAt).format("dddd Do MMMM YYYY")}
          </MainText>
          {!!skill && (
            <SkillLabel text={selectLanguage({ text: skill, language })} />
          )}
        </Box>
        <MainText variant="l3-regular" numberOfLines={2}>
          {removeHtmlTags(selectLanguage({ text: content?.title, language }))}
        </MainText>
        <Box
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          marginTop="sm"
        >
          <Box flexDirection="row">
            <Box flexDirection="row" alignItems="center" marginRight="s">
              <MediaIcon width={normalize(18)} height={normalize(18)} />
              <MainText variant="l2-medium">
                {files.length + " " + i18n.t("documents")}
              </MainText>
            </Box>
            {getRatingComponent()}
            <Box flexDirection="row" alignItems="center">
              <FastImage source={StarImage} style={{ height: 20, width: 15 }} />
              <MainText variant="l2-medium" marginLeft="xxs">
                {rating}
              </MainText>
            </Box>
          </Box>
          {hasReview && <ReviewStatusChip status={status} />}
        </Box>
      </Box>
    </PressableOpacity>
  );
};

export default HistoricalBox;
